import { useState, useEffect, useMemo } from 'react';
import { Search, Trash2, Percent, PackageSearch } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const toLocalIso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const isoPlus = (days) => { const d = new Date(); d.setDate(d.getDate() + days); return toLocalIso(d); };
const r2 = (v) => Math.round(v * 100) / 100;

// Formulaire promotion : période + sélection de produits avec prix promo par palier.
export const PromotionForm = ({ promo, onCancel, onSave }) => {
  const [name, setName] = useState(promo?.name || 'Déstockage de la quinzaine');
  const [startsAt, setStartsAt] = useState(promo?.starts_at || isoPlus(0));
  const [endsAt, setEndsAt] = useState(promo?.ends_at || isoPlus(13));
  const [lines, setLines] = useState(() => (promo?.items || []).map(it => ({
    product_id: it.product_id, name: it.product?.name, brand: it.product?.brand, stock: it.product?.stock, cost_price: it.product?.cost_price,
    orig: { detail: it.product?.price_detail, semi_gros: it.product?.price_semi_gros, gros: it.product?.price_gros },
    price_detail: it.price_detail ?? '', price_semi_gros: it.price_semi_gros ?? '', price_gros: it.price_gros ?? '',
  })));
  const [suggestions, setSuggestions] = useState([]);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [percent, setPercent] = useState(20);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/admin/promotions/suggestions`).then(r => setSuggestions(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (search.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(() => {
      axios.get(`${API_URL}/api/products?search=${encodeURIComponent(search)}`).then(r => setResults(r.data.slice(0, 8))).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [search]);

  const ids = useMemo(() => new Set(lines.map(l => l.product_id)), [lines]);

  const addProduct = (p) => {
    if (ids.has(p.id)) return;
    const orig = { detail: p.price_detail_original ?? p.price_detail, semi_gros: p.price_semi_gros_original ?? p.price_semi_gros, gros: p.price_gros_original ?? p.price_gros };
    const f = 1 - percent / 100;
    setLines(prev => [...prev, {
      product_id: p.id, name: p.name, brand: p.brand, stock: p.stock, cost_price: p.cost_price, orig,
      price_detail: orig.detail != null ? r2(orig.detail * f) : '', price_semi_gros: orig.semi_gros != null ? r2(orig.semi_gros * f) : '', price_gros: orig.gros != null ? r2(orig.gros * f) : '',
    }]);
    setSearch(''); setResults([]);
  };

  const applyPercent = () => {
    const f = 1 - percent / 100;
    setLines(prev => prev.map(l => ({ ...l,
      price_detail: l.orig.detail != null ? r2(l.orig.detail * f) : l.price_detail,
      price_semi_gros: l.orig.semi_gros != null ? r2(l.orig.semi_gros * f) : l.price_semi_gros,
      price_gros: l.orig.gros != null ? r2(l.orig.gros * f) : l.price_gros,
    })));
  };

  const setPrice = (i, k, v) => setLines(prev => prev.map((l, idx) => idx === i ? { ...l, [k]: v } : l));

  const submit = async () => {
    if (lines.length === 0) { toast.error('Ajoutez au moins un produit'); return; }
    setSaving(true);
    try {
      await onSave({
        name, starts_at: startsAt, ends_at: endsAt, active: promo ? promo.active : true,
        items: lines.map(l => ({
          product_id: l.product_id,
          price_detail: l.price_detail === '' ? null : Number(l.price_detail),
          price_semi_gros: l.price_semi_gros === '' ? null : Number(l.price_semi_gros),
          price_gros: l.price_gros === '' ? null : Number(l.price_gros),
        })),
      }, promo?.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const inp = 'w-full bg-transparent border border-white/10 focus:border-[color:var(--color-primary)] text-white px-3 py-2 outline-none text-sm';

  return (
    <div className="space-y-6" data-testid="promo-form">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 border border-white/10 bg-[color:var(--color-surface)] p-5">
        <div>
          <label className="text-zinc-500 text-xs uppercase tracking-widest mb-1 block">Nom de la promo</label>
          <input value={name} onChange={(e) => setName(e.target.value)} className={inp} data-testid="promo-name" />
        </div>
        <div>
          <label className="text-zinc-500 text-xs uppercase tracking-widest mb-1 block">Début</label>
          <input type="date" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} className={inp} data-testid="promo-start" />
        </div>
        <div>
          <label className="text-zinc-500 text-xs uppercase tracking-widest mb-1 block">Fin (incluse)</label>
          <div className="flex gap-2">
            <input type="date" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} className={inp} data-testid="promo-end" />
            <button type="button" onClick={() => { const d = new Date(`${startsAt}T12:00:00`); d.setDate(d.getDate() + 13); setEndsAt(toLocalIso(d)); }}
              className="whitespace-nowrap text-xs border border-white/10 text-zinc-300 px-2 hover:border-[color:var(--color-primary)]" data-testid="promo-14days">14 j</button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <div className="border border-white/10 bg-[color:var(--color-surface)]">
            <div className="flex flex-wrap items-center justify-between gap-3 p-3 border-b border-white/5">
              <p className="text-white text-sm">Produits en promo ({lines.length})</p>
              <div className="flex items-center gap-2">
                <Percent className="w-3.5 h-3.5 text-zinc-500" />
                <input type="number" min="1" max="90" value={percent} onChange={(e) => setPercent(Number(e.target.value))}
                  className="w-16 bg-transparent border border-white/10 text-white text-xs px-2 py-1 outline-none" data-testid="promo-percent" />
                <button type="button" onClick={applyPercent} className="text-xs border border-[color:rgb(var(--primary-rgb)/0.50)] text-[color:var(--color-primary)] px-2.5 py-1 hover:bg-[color:var(--color-primary)] hover:text-black" data-testid="promo-apply-percent">
                  Appliquer -{percent}% à tout
                </button>
              </div>
            </div>
            <div className="relative p-3 border-b border-white/5">
              <Search className="w-4 h-4 text-zinc-500 absolute left-6 top-1/2 -translate-y-1/2" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Ajouter un produit (nom, marque, code barre)..."
                className={`${inp} pl-9`} data-testid="promo-search" />
              {results.length > 0 && (
                <ul className="absolute left-3 right-3 top-full z-20 bg-[color:var(--color-surface)] border border-white/10 max-h-64 overflow-y-auto" data-testid="promo-search-results">
                  {results.map(p => (
                    <li key={p.id}>
                      <button type="button" onClick={() => addProduct(p)} disabled={ids.has(p.id)}
                        className="w-full text-left px-3 py-2 hover:bg-white/5 disabled:opacity-40 flex justify-between text-sm" data-testid={`promo-add-${p.id}`}>
                        <span className="text-white truncate"><span className="text-zinc-500 text-xs uppercase mr-2">{p.brand}</span>{p.name}</span>
                        <span className="text-zinc-500 text-xs">stock {p.stock} · {p.price_detail}€</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {lines.length === 0 ? (
              <p className="text-zinc-500 text-sm p-6 text-center">Aucun produit. Utilisez la recherche ou les suggestions « Stock dormant ».</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="promo-lines">
                  <thead className="text-zinc-500 text-[10px] uppercase tracking-wider">
                    <tr><th className="text-left p-3">Produit</th><th className="text-right p-3">Détail</th><th className="text-right p-3">Semi-gros</th><th className="text-right p-3">Gros</th><th></th></tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {lines.map((l, i) => (
                      <tr key={l.product_id} data-testid={`promo-line-${l.product_id}`}>
                        <td className="p-3">
                          <p className="text-white truncate max-w-[220px]">{l.name}</p>
                          <p className="text-zinc-500 text-xs">{l.brand} · stock {l.stock}{l.cost_price ? ` · achat ${l.cost_price}€` : ''}</p>
                        </td>
                        {['detail', 'semi_gros', 'gros'].map(k => (
                          <td key={k} className="p-3 text-right">
                            <p className="text-zinc-400 text-xs line-through">{l.orig[k] != null ? `${l.orig[k]}€` : '—'}</p>
                            <input type="number" step="0.01" min="0" value={l[`price_${k}`]} onChange={(e) => setPrice(i, `price_${k}`, e.target.value)}
                              className={`w-20 text-right bg-transparent border px-2 py-1 outline-none ${l.cost_price && Number(l[`price_${k}`]) < Number(l.cost_price) ? 'border-red-500/60 text-red-300' : 'border-white/10 text-white focus:border-[color:var(--color-primary)]'}`}
                              data-testid={`promo-price-${k}-${l.product_id}`} />
                          </td>
                        ))}
                        <td className="p-3">
                          <button type="button" onClick={() => setLines(prev => prev.filter((_, idx) => idx !== i))} className="text-zinc-500 hover:text-red-400" data-testid={`promo-remove-${l.product_id}`}><Trash2 className="w-4 h-4" /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="text-zinc-600 text-[11px] p-3">Un prix en rouge est inférieur au prix d'achat (vente à perte assumée).</p>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-3">
            <button type="button" onClick={onCancel} className="px-4 py-2 border border-white/10 text-zinc-400 hover:text-white text-sm">Annuler</button>
            <button type="button" onClick={submit} disabled={saving} className="px-5 py-2 bg-[color:var(--color-primary)] text-black text-sm hover:bg-[color:var(--color-primary-hover)] disabled:opacity-50" data-testid="promo-save-btn">
              {saving ? 'Enregistrement...' : promo ? 'Enregistrer' : 'Lancer la promotion'}
            </button>
          </div>
        </div>

        <aside className="border border-white/10 bg-[color:var(--color-surface)] p-4 h-fit">
          <h3 className="text-white text-sm flex items-center gap-2 mb-1"><PackageSearch className="w-4 h-4 text-orange-400" /> Stock dormant</h3>
          <p className="text-zinc-500 text-xs mb-3">Beaucoup de stock, peu de ventes sur 60 jours. Cliquez pour ajouter (-{percent}% appliqué).</p>
          <ul className="divide-y divide-white/5 max-h-[520px] overflow-y-auto" data-testid="promo-suggestions">
            {suggestions.map(p => (
              <li key={p.id}>
                <button type="button" onClick={() => addProduct(p)} disabled={ids.has(p.id)}
                  className="w-full text-left py-2 flex items-center gap-2 hover:bg-white/5 disabled:opacity-30" data-testid={`promo-suggest-${p.id}`}>
                  <div className="w-9 h-10 bg-black overflow-hidden flex-shrink-0">{p.image_url && <img src={p.image_url} alt="" className="w-full h-full object-cover" />}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-xs truncate">{p.name}</p>
                    <p className="text-zinc-500 text-[10px]">stock <span className="text-orange-400">{p.stock}</span> · {p.sold_recent} vendu(s){p.in_promo ? ' · déjà en promo' : ''}</p>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </aside>
      </div>
    </div>
  );
};
